import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Settings, ChevronLeft, PlusCircle, MinusCircle, TrendingDown, RefreshCw, ArrowRightLeft, X } from 'lucide-react';
import { subDays } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Transaction } from '@/types/asset';

// Helper function to get badge variant based on transaction status - extracted to fix nested ternary (S3358)
function getTransactionBadgeVariant(status: string): 'default' | 'secondary' | 'outline' {
  if (status === 'posted') {
    return 'default';
  }
  if (status === 'approved') {
    return 'secondary';
  }
  return 'outline';
}

interface RecentActivityCardProps {
  readonly transactions: Transaction[];
  readonly isCollapsed?: boolean;
  readonly onToggleCollapse?: () => void;
}

type TimeRange = 'week' | '14days' | 'month';

const TIME_RANGE_CONFIG = {
  week: { label: 'Last Week', days: 7 },
  '14days': { label: 'Last 14 Days', days: 14 },
  month: { label: 'Last Month', days: 30 },
};

// Transaction type colors and icons
const getTransactionStyle = (type: string) => {
  const styles: Record<string, { bgColor: string; borderColor: string; icon: React.ComponentType<{ className?: string }> }> = {
    addition: {
      bgColor: 'bg-green-50 dark:bg-green-950/30',
      borderColor: 'border-green-200 dark:border-green-800',
      icon: PlusCircle
    },
    acquisition: { 
      bgColor: 'bg-green-50 dark:bg-green-950/30', 
      borderColor: 'border-green-200 dark:border-green-800',
      icon: PlusCircle 
    },
    disposal: { 
      bgColor: 'bg-red-50 dark:bg-red-950/30', 
      borderColor: 'border-red-200 dark:border-red-800',
      icon: MinusCircle
    },
    depreciation: {
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
      borderColor: 'border-blue-200 dark:border-blue-800',
      icon: TrendingDown
    },
    revaluation: {
      bgColor: 'bg-purple-50 dark:bg-purple-950/30',
      borderColor: 'border-purple-200 dark:border-purple-800',
      icon: RefreshCw
    },
    transfer: {
      bgColor: 'bg-orange-50 dark:bg-orange-950/30',
      borderColor: 'border-orange-200 dark:border-orange-800',
      icon: ArrowRightLeft
    },
  };
  return styles[type.toLowerCase()] || {
    bgColor: 'bg-gray-50 dark:bg-gray-950/30',
    borderColor: 'border-gray-200 dark:border-gray-800',
    icon: PlusCircle
  };
};

export function RecentActivityCard({ transactions, isCollapsed = false, onToggleCollapse }: RecentActivityCardProps): React.ReactElement {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('week');

  console.log('RecentActivityCard - Received transactions:', transactions?.length ?? 0);

  // Helper function to convert status to camel case
  const formatStatusText = (status: string): string => {
    return status
      .split(/[\s_-]+/) // Split by space, underscore, or hyphen
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Filter transactions based on selected time range
  const getFilteredTransactions = () => {
    const days = TIME_RANGE_CONFIG[timeRange].days;
    const cutoffDate = subDays(new Date(), days);

    console.log('Filtering transactions:', {
      totalTransactions: transactions?.length ?? 0,
      timeRange,
      days,
      cutoffDate: cutoffDate.toISOString()
    });

    const filtered = transactions
      .filter(transaction => {
        const transDate = new Date(transaction.transactionDate);
        const isAfterCutoff = transDate >= cutoffDate;
        return isAfterCutoff;
      })
      .sort((a: any, b: any) =>
        new Date(b.transactionDate).getTime() - new Date(a.transactionDate).getTime()
      );

    return filtered;
  };

  const recentActivity = getFilteredTransactions();

  // Collapsed view - just a vertical bar with toggle button
  if (isCollapsed) {
    return (
      <div className="relative h-full bg-muted/30 border-l flex flex-col items-center py-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onToggleCollapse}
          className="h-8 w-8"
          title="Expand Recent Activity"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div className="mt-4 writing-mode-vertical text-sm font-semibold">
          <span style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}>
            Recent Activity
          </span>
        </div>
      </div>
    );
  }

  // Expanded view - full panel
  return (
    <div className="relative h-full bg-muted/30 border-l flex flex-col">
      {/* Header with collapse button */}
      <div className="flex-shrink-0 border-b border-border/40 p-4 sticky top-0 bg-muted/30 z-10">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold">Recent Activity</h3>

          {/* Settings Cog with Dropdown and Close Button */}
          <div className="flex items-center gap-1">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
                >
                  <Settings className="h-3.5 w-3.5" />
                  <span className="sr-only">Time range settings</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => setTimeRange('week')}
                  className={timeRange === 'week' ? 'bg-accent' : ''}
                >
                  Last Week
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTimeRange('14days')}
                  className={timeRange === '14days' ? 'bg-accent' : ''}
                >
                  Last 14 Days
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setTimeRange('month')}
                  className={timeRange === 'month' ? 'bg-accent' : ''}
                >
                  Last Month
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Close Button */}
            {onToggleCollapse && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onToggleCollapse}
                className="h-7 w-7 text-muted-foreground hover:text-foreground flex-shrink-0"
                title="Close Recent Activity"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content area with scroll */}
      <div className="flex-1 overflow-y-auto p-4">
        {recentActivity.length === 0 ? (
          <div className="text-center py-8">
            <PlusCircle className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">
              No activity found for the selected time period
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {recentActivity.map((transaction) => {
              const transactionStyle = getTransactionStyle(transaction.type);
              const Icon = transactionStyle.icon;

              // Get icon color based on transaction type
              const getIconColor = (type: string) => {
                switch (type.toLowerCase()) {
                  case 'addition':
                  case 'acquisition':
                    return 'text-green-600';
                  case 'disposal':
                    return 'text-red-600';
                  case 'depreciation':
                    return 'text-blue-600';
                  case 'revaluation':
                    return 'text-purple-600';
                  case 'transfer':
                    return 'text-orange-600';
                  default:
                    return 'text-gray-600';
                }
              };

              return (
                <button
                  type="button"
                  key={transaction.id}
                  onClick={() => navigate(`/enquiry?transactionId=${transaction.id}`)}
                  className="p-3 rounded-lg bg-card border border-border/40 hover:border-primary/50 transition-all duration-200 cursor-pointer group w-full text-left"
                >
                  <div className="flex gap-2">
                    <div className={`mt-0.5 ${getIconColor(transaction.type)}`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="space-y-1">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <span className="text-xs font-medium group-hover:text-primary transition-colors line-clamp-1">
                              {transaction.assetName}
                            </span>
                            <p className="text-xs text-muted-foreground mt-0.5 capitalize">
                              {transaction.type}
                            </p>
                          </div>
                          <Badge
                            variant={getTransactionBadgeVariant(transaction.status)}
                            className="text-[10px] px-1.5 py-0 h-5 flex-shrink-0"
                          >
                            {formatStatusText(transaction.status)}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs text-muted-foreground">
                            {(() => {
                              const date = new Date(transaction.transactionDate);
                              const now = new Date();
                              const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
                              
                              if (diffInHours < 24) {
                                return date.toLocaleTimeString('en-US', { 
                                  hour: 'numeric', 
                                  minute: '2-digit',
                                  hour12: true 
                                });
                              } else if (diffInHours < 48) {
                                return 'Yesterday';
                              } else {
                                return date.toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric' 
                                });
                              }
                            })()}
                          </span>
                          <span className="text-xs font-semibold text-foreground">
                            £{transaction.amount.toLocaleString('en-GB', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
